import React, { useEffect, useRef, useState, useMemo } from 'react';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';
import {
    X,
    Search,
    CheckCircle2,
    Mail,
    Calendar,
    Linkedin,
    Sparkles,
    MapPin,
    Award,
    Send,
    MessageCircle,
    Copy,
    ArrowRight,
    Users
} from 'lucide-react';

// ── Paleta Co Marca ──
const colors = {
    primary: '#61257B',
    primaryDark: '#420f54',
    primaryLight: '#C1348B',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#ffd97c',
    accentDark: '#EA8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Tipos ──
type MemberType = 'fundador' | 'lider' | 'equipo' | 'mentor';

interface Member {
    id: string;
    name: string;
    role: string;
    area: string;
    type: MemberType;
    bio: string;
    fullBio?: string;
    since: string;
    avatar: string;
    initials: string;
    color: string;
    gradient: string;
    location?: string;
    availability?: string;
    highlights: string[];
    skills?: string[];
    achievements?: string[];
    social?: {
        linkedin?: string;
        email?: string;
        whatsapp?: string;
    };
}

// ── Datos: Fundadores ──
const founders: Member[] = [
    {
        id: 'f1', type: 'fundador',
        name: 'Alejandro Marca Torrez',
        role: 'CEO & Co-fundador',
        area: 'Visión & Estrategia',
        bio: 'Emprendedor serial con 15 años de experiencia en el ecosistema boliviano. Fundó Co Marca con la visión de crear el hub de innovación más importante de la región andina. Mentor de más de 80 startups.',
        fullBio: 'Alejandro ha sido pionero en la creación de comunidades de coworking y aceleración en Bolivia. Ha estructurado alianzas con fondos de inversión de la región andina y fondos multilaterales. Su misión es democratizar el acceso a capital semilla, mentoría de alto calibre y espacios de trabajo del más alto estándar internacional.',
        since: '2019',
        avatar: '/images/miembros/alejandro-marca.jpg',
        initials: 'AM',
        color: '#61257B',
        gradient: 'linear-gradient(145deg, #420f54, #61257B)',
        location: 'La Paz, Bolivia',
        availability: 'Disponible para mentorías estratégicas',
        highlights: ['Co-fundador', '15 años emprendiendo', '80+ startups mentoreadas'],
        skills: ['Venture Capital', 'Estrategia de Crecimiento', 'Negociación', 'Modelos de Negocio'],
        achievements: [
            'Lideró la creación del primer coworking enfocado en escalabilidad tecnológica de La Paz.',
            'Asesoró a 4 startups bolivianas en su proceso de internacionalización a Perú y Chile.',
            'Speaker principal en más de 30 conferencias de innovación y venture capital.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/alejandro-marca-ejemplo',
            email: 'amarca@comarca.bo',
            whatsapp: '+59170010001'
        }
    },
    {
        id: 'f2', type: 'fundador',
        name: 'Daniela Ríos Saavedra',
        role: 'COO & Co-fundadora',
        area: 'Operaciones & Comunidad',
        bio: 'Especialista en gestión de comunidades y operaciones. Diseñó el modelo de membresías y los programas de aceleración que han impactado a cientos de emprendedores bolivianos desde 2019.',
        fullBio: 'Con una sólida formación en gestión de operaciones de hospitalidad y espacios compartidos, Daniela supervisa la infraestructura, la experiencia del miembro y la expansión de los programas comunitarios. Es la arquitecta de la cultura colaborativa de Co Marca.',
        since: '2019',
        avatar: '/images/miembros/daniela-rios.jpg',
        initials: 'DR',
        color: '#C1348B',
        gradient: 'linear-gradient(145deg, #61257B, #C1348B)',
        location: 'La Paz, Bolivia',
        availability: 'Reuniones de alianza y cultura de comunidad',
        highlights: ['Co-fundadora', 'Diseño de programas', '300+ miembros gestionados'],
        skills: ['Operaciones Coworking', 'Community Building', 'Gestión de Talento', 'Experiencia del Cliente'],
        achievements: [
            'Mantuvo una tasa de retención de miembros superior al 91% durante 4 años consecutivos.',
            'Coordinó la apertura de 3 pisos de innovación en la sede central.',
            'Desarrolló el programa de bienestar y networking semanal "Martes Co Marca".'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/daniela-rios-ejemplo',
            email: 'drios@comarca.bo',
            whatsapp: '+59170010002'
        }
    },
];

// ── Datos: Líderes y Directores ──
const leaders: Member[] = [
    {
        id: 'l1', type: 'lider',
        name: 'Gabriel Condori Quispe',
        role: 'Director de Tecnología',
        area: 'Tech & Innovación',
        bio: 'Ingeniero de software con experiencia en Silicon Valley. Lidera la transformación digital de Co Marca y el desarrollo de herramientas para la comunidad.',
        fullBio: 'Gabriel lideró equipos de ingeniería distribuidos en EE.UU. antes de regresar a Bolivia para impulsar la tecnología local. Supervisa la infraestructura cloud de Co Marca, las apps para miembros y apoya técnicamente a las startups incubadas.',
        since: '2020',
        avatar: '/images/miembros/gabriel-condori.jpg',
        initials: 'GC',
        color: '#C1348B',
        gradient: 'linear-gradient(145deg, #420f54, #C1348B)',
        location: 'La Paz (Híbrido)',
        availability: 'Asesorías en arquitectura cloud y stack tecnológico',
        highlights: ['Ex Silicon Valley', 'Transformación digital', 'CTO desde 2020'],
        skills: ['React & Node.js', 'AWS Cloud Architect', 'Microservicios', 'DevOps & CI/CD'],
        achievements: [
            'Desarrolló la plataforma digital integral de reservas y control de accesos de Co Marca.',
            'Mentor técnico de más de 25 productos de software creados en la comunidad.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/gabriel-condori-ejemplo',
            email: 'gcondori@comarca.bo',
            whatsapp: '+59170010003'
        }
    },
    {
        id: 'l2', type: 'lider',
        name: 'Valentina Cruz Montaño',
        role: 'Directora de Marketing',
        area: 'Marketing & Marca',
        bio: 'Brand strategist con enfoque en ecosistemas emprendedores. Ha posicionado a Co Marca como la referencia de innovación en Bolivia y construido una comunidad digital de 20,000+ seguidores.',
        fullBio: 'Especialista en narrativas de marca y adquisición omnicanal. Valentina ha liderado el relanzamiento de la identidad de Co Marca y las campañas de captación de empresas que hoy forman parte del ecosistema.',
        since: '2021',
        avatar: '/images/miembros/valentina-cruz.jpg',
        initials: 'VC',
        color: '#61257B',
        gradient: 'linear-gradient(145deg, #420f54, #61257B)',
        location: 'La Paz, Bolivia',
        availability: 'Consultoría en branding y estrategia digital',
        highlights: ['20K+ comunidad digital', 'Brand strategy', '4 premios de marketing'],
        skills: ['Branding & PR', 'Growth Marketing', 'Content Strategy', 'Social Media Analytics'],
        achievements: [
            'Crecimiento de 340% en alcance orgánico de la marca en redes sociales.',
            'Ganadora del reconocimiento Ecosistema Innovador 2023 por mejor campaña de impacto.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/valentina-cruz-ejemplo',
            email: 'vcruz@comarca.bo',
            whatsapp: '+59170010004'
        }
    },
    {
        id: 'l3', type: 'lider',
        name: 'Sebastián Alvarado Torres',
        role: 'Director de Programas',
        area: 'Formación & Aceleración',
        bio: 'Especialista en diseño de programas de aceleración. Creó la metodología Nexo que ha llevado a 4 cohortes a presentar ante inversores con resultados récord.',
        fullBio: 'Apasionado por la educación ejecutiva y el venture building. Sebastián conecta a fundadores en etapas tempranas con ángeles inversionistas y diseña los talleres de formación continua de Co Marca.',
        since: '2020',
        avatar: '/images/miembros/sebastian-alvarado.jpg',
        initials: 'SA',
        color: '#EA8406',
        gradient: 'linear-gradient(145deg, #2a0f3d, #EA8406)',
        location: 'La Paz, Bolivia',
        availability: 'Revisiones de Pitch Deck y preparación de rondas',
        highlights: ['4 cohortes lideradas', 'Metodología Nexo', 'USD 120K+ levantados'],
        skills: ['Pitch Training', 'Incubación de Negocios', 'Diseño Curricular', 'Finanzas de Startups'],
        achievements: [
            'Más de 40 empresas graduadas del programa de aceleración Nexo.',
            'Facilitó conexiones que resultaron en más de USD 120,000 en inversión ángel.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/sebastian-alvarado-ejemplo',
            email: 'salvarado@comarca.bo',
            whatsapp: '+59170010005'
        }
    },
    {
        id: 'l4', type: 'lider',
        name: 'Mariana Peña Bustamante',
        role: 'Directora Comercial',
        area: 'Ventas & Partnerships',
        bio: 'Ejecutiva comercial con track record en empresas multinacionales. Llevó a Co Marca de 20 a 300+ miembros activos en 3 años y construyó alianzas con las principales corporaciones bolivianas.',
        fullBio: 'Mariana lidera las relaciones B2B y los acuerdos corporativos. Su experiencia permite diseñar soluciones espaciales y de membresía a la medida de corporaciones multinacionales y PyMEs en rápida expansión.',
        since: '2021',
        avatar: '/images/miembros/mariana-pena.jpg',
        initials: 'MP',
        color: '#420f54',
        gradient: 'linear-gradient(145deg, #451759, #61257B)',
        location: 'La Paz / Santa Cruz',
        availability: 'Alianzas corporativas y planes empresariales',
        highlights: ['300+ miembros captados', 'Alianzas corporativas', 'Top Sales 2023'],
        skills: ['B2B Sales', 'Key Account Management', 'Negociación Corporativa', 'Revenue Operations'],
        achievements: [
            'Cerró acuerdos con 18 corporaciones líderes para el uso de oficinas dedicadas.',
            'Incrementó la ocupación de espacios privados al 96% sostenido.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/mariana-pena-ejemplo',
            email: 'mpena@comarca.bo',
            whatsapp: '+59170010006'
        }
    },
];

// ── Datos: Equipo Operativo ──
const team: Member[] = [
    {
        id: 't1', type: 'equipo',
        name: 'Lucía Mamani',
        role: 'Community Manager',
        area: 'Comunidad',
        bio: 'El alma de la comunidad Co Marca. Organiza los eventos mensuales y mantiene vivo el espíritu colaborativo entre todos los miembros.',
        fullBio: 'Lucía es el primer rostro que te recibe en Co Marca. Conoce a cada miembro por su nombre y conecta diariamente a profesionales con intereses comunes.',
        since: '2022',
        avatar: '/images/miembros/lucia-mamani.jpg',
        initials: 'LM',
        color: '#61257B',
        gradient: 'linear-gradient(135deg, #420f54, #61257B)',
        location: 'La Paz, Bolivia',
        availability: 'Conexión entre miembros y bienvenida',
        highlights: ['60+ eventos organizados', 'Comunidad activa', 'Facilitadora'],
        skills: ['Gestión Comunitaria', 'Event Hosting', 'Resolución de Conflictos', 'Networking Activo'],
        social: { linkedin: 'https://linkedin.com/in/lucia-mamani-ejemplo', email: 'lmamani@comarca.bo' }
    },
    {
        id: 't2', type: 'equipo',
        name: 'Emilio Vargas',
        role: 'Encargado de Espacios',
        area: 'Operaciones & Facility',
        bio: 'Garantiza que cada rincón de Co Marca esté impecable y funcional. El responsable de que la tecnología y conectividad siempre funcionen al 100%.',
        fullBio: 'Emilio se asegura de que las salas de juntas, la fibra óptica de respaldo y las zonas ergonómicas funcionen sin interrupciones.',
        since: '2021',
        avatar: '/images/miembros/emilio-vargas.jpg',
        initials: 'EV',
        color: '#C1348B',
        gradient: 'linear-gradient(135deg, #451759, #C1348B)',
        location: 'La Paz, Bolivia',
        availability: 'Soporte técnico y logística de salas',
        highlights: ['0 fallas técnicas 2024', 'Gestión de 3 pisos', 'Soporte inmediato'],
        skills: ['Facility Management', 'Redes de Alta Disponibilidad', 'Seguridad Física', 'Acústica'],
        social: { linkedin: 'https://linkedin.com/in/emilio-vargas-ejemplo', email: 'evargas@comarca.bo' }
    },
    {
        id: 't3', type: 'equipo',
        name: 'Camila Orellana',
        role: 'Diseñadora UX/UI',
        area: 'Diseño & Producto',
        bio: 'Diseña las experiencias digitales y físicas de Co Marca. Detrás de la plataforma web, la señalética y todos los materiales visuales.',
        fullBio: 'Camila combina principios de diseño centrado en el usuario y diseño biofílico para hacer que trabajar en Co Marca sea inspirador tanto en la pantalla como en los escritorios.',
        since: '2023',
        avatar: '/images/miembros/camila-orellana.jpg',
        initials: 'CO',
        color: '#61257B',
        gradient: 'linear-gradient(135deg, #420f54, #61257B)',
        location: 'La Paz, Bolivia',
        availability: 'Consultoría visual y prototipado rápido',
        highlights: ['Diseño de plataforma', 'Brand visual 2024', 'UX research'],
        skills: ['Figma Pro', 'Design Systems', 'User Testing', 'Diseño Editorial'],
        social: { linkedin: 'https://linkedin.com/in/camila-orellana-ejemplo', email: 'corellana@comarca.bo' }
    },
    {
        id: 't4', type: 'equipo',
        name: 'Rodrigo Sánchez',
        role: 'Asesor de Negocios',
        area: 'Programas & Startups',
        bio: 'Acompaña a emprendedores en sus primeros pasos. Ha mentoreado a más de 40 startups en validación de producto y primeros clientes.',
        fullBio: 'Especialista en metodologías Lean Startup y Customer Discovery. Ayuda a los equipos a validar hipótesis antes de quemar capital.',
        since: '2022',
        avatar: '/images/miembros/rodrigo-sanchez.jpg',
        initials: 'RS',
        color: '#FFD97C',
        gradient: 'linear-gradient(135deg, #301045, #C1348B)',
        location: 'La Paz, Bolivia',
        availability: 'Sesiones de validación 1 a 1',
        highlights: ['40+ mentorías', 'Lean Startup', 'Asesor Nexo'],
        skills: ['Lean Canvas', 'Customer Discovery', 'Proyecciones Financieras', 'Unit Economics'],
        social: { linkedin: 'https://linkedin.com/in/rodrigo-sanchez-ejemplo', email: 'rsanchez@comarca.bo' }
    },
    {
        id: 't5', type: 'equipo',
        name: 'Andrea Flores',
        role: 'Coordinadora de Eventos',
        area: 'Comunidad & Cultura',
        bio: 'Produce todos los eventos de Co Marca con precisión quirúrgica. Hackathons, conferencias y masterclasses ejecutadas sin fallas.',
        fullBio: 'Andrea gestiona la agenda cultural y de networking del hub, asegurando la convocatoria y transmisión en vivo para toda la comunidad.',
        since: '2023',
        avatar: '/images/miembros/andrea-flores.jpg',
        initials: 'AF',
        color: '#420f54',
        gradient: 'linear-gradient(135deg, #451759, #420f54)',
        location: 'La Paz, Bolivia',
        availability: 'Coordinación y co-producción de eventos de comunidad',
        highlights: ['60+ eventos producidos', 'Logística cero errores', 'Difusión masiva'],
        skills: ['Event Production', 'Streaming & Audio', 'Gestión de Speakers', 'Catering & Logística'],
        social: { linkedin: 'https://linkedin.com/in/andrea-flores-ejemplo', email: 'aflores@comarca.bo' }
    },
    {
        id: 't6', type: 'equipo',
        name: 'Tomás Gutiérrez',
        role: 'Desarrollador Backend',
        area: 'Tecnología',
        bio: 'Construye y mantiene la infraestructura tecnológica que conecta a toda la comunidad Co Marca y sus sistemas de reserva en tiempo real.',
        fullBio: 'Desarrollador enfocado en alta concurrencia, APIs seguras e integraciones de pasarelas de pago y facturación boliviana computarizada.',
        since: '2024',
        avatar: '/images/miembros/tomas-gutierrez.jpg',
        initials: 'TG',
        color: '#C1348B',
        gradient: 'linear-gradient(135deg, #61257B, #C1348B)',
        location: 'La Paz, Bolivia',
        availability: 'Integraciones técnicas y soporte de API',
        highlights: ['Plataforma de miembros', 'APIs & Webhooks', 'Seguridad'],
        skills: ['Python / Django', 'PostgreSQL', 'Docker', 'Facturación SIAT'],
        social: { linkedin: 'https://linkedin.com/in/tomas-gutierrez-ejemplo', email: 'tgutierrez@comarca.bo' }
    },
];

// ── Datos: Mentores & Red Asesora (Nuevos ejemplos de alto calibre) ──
const mentors: Member[] = [
    {
        id: 'm1', type: 'mentor',
        name: 'Dra. Valeria Montaño',
        role: 'Inversionista Ángel & Asesora Fintech',
        area: 'Venture Capital & Finanzas',
        bio: 'Ex-VP de Innovación en banca privada boliviana y miembro activa del Club de Inversionistas Ángeles de Bolivia. Asesora en rondas semilla y estructuración de modelos financieros.',
        fullBio: 'Con más de 16 años liderando la banca digital y productos de inclusión financiera en los Andes, Valeria aporta visión estratégica y conexiones directas con sindicatos de capital ángel de Bolivia, Chile y Colombia.',
        since: '2021',
        avatar: '/images/miembros/valeria-montano.jpg',
        initials: 'VM',
        color: '#61257B',
        gradient: 'linear-gradient(135deg, #420f54, #61257B)',
        location: 'La Paz / Santa Cruz',
        availability: '🟢 Mentorías mensuales para startups en etapa seed',
        highlights: ['12+ Startups Invertidas', 'Red Ángel Bolivia', 'Ex-Banca Privada'],
        skills: ['Venture Capital', 'Due Diligence', 'Fintech & Pagos', 'Gobernanza Corporativa'],
        achievements: [
            'Participó como co-inversionista en 4 rondas semilla destacadas del cono sur.',
            'Mentora del año 2023 elegida por los fundadores de la red Co Marca.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/valeria-montano-ejemplo',
            email: 'vmontano.mentor@comarca.bo',
            whatsapp: '+59170010007'
        }
    },
    {
        id: 'm2', type: 'mentor',
        name: 'Dr. Marcelo Claros',
        role: 'Asesor Legal en Venture & IP',
        area: 'Derecho Tecnológico & Startups',
        bio: 'Abogado con maestría en EE.UU., experto en estructuración de vehículos societarios (Delaware C-Corp, SAS), acuerdos de socios (vesting) y protección de propiedad intelectual.',
        fullBio: 'Marcelo asesora a startups tecnológicas desde su incorporación hasta la firma de instrumentos convertibles (SAFE, KISS) y rondas institucionales de inversión transfronteriza.',
        since: '2022',
        avatar: '/images/miembros/marcelo-claros.jpg',
        initials: 'MC',
        color: '#C1348B',
        gradient: 'linear-gradient(135deg, #61257B, #C1348B)',
        location: 'La Paz, Bolivia',
        availability: '🟢 Consultas legales y revisión de acuerdos de socios',
        highlights: ['Delaware C-Corp Specialist', '50+ Contratos SAFE', 'IP & Patentes'],
        skills: ['SAFE & Convertible Notes', 'Cap Table Management', 'Propiedad Intelectual', 'Compliance'],
        achievements: [
            'Asesoró la estructuración legal internacional de 3 de las startups bolivianas más reconocidas.',
            'Docente de Posgrado en Regulación de Nuevas Tecnologías.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/marcelo-claros-ejemplo',
            email: 'mclaros.legal@comarca.bo',
            whatsapp: '+59170010008'
        }
    },
    {
        id: 'm3', type: 'mentor',
        name: 'Ing. Rodrigo Peñaranda',
        role: 'Tech Advisor & Cloud Architect',
        area: 'Inteligencia Artificial & Infraestructura',
        bio: 'Arquitecto cloud certificado por AWS y Google Cloud. Asesora a fundadores técnicos en escalabilidad de bases de datos, microservicios y despliegue seguro de modelos de IA.',
        fullBio: 'Rodrigo cuenta con una trayectoria consolidada guiando a startups de alto volumen transaccional en la optimización de costos en la nube y la adopción pragmática de LLMs y GenAI en productos existentes.',
        since: '2022',
        avatar: '/images/miembros/rodrigo-penaranda.jpg',
        initials: 'RP',
        color: '#EA8406',
        gradient: 'linear-gradient(135deg, #2a0f3d, #EA8406)',
        location: 'La Paz (Remoto)',
        availability: '🟢 Auditorías de arquitectura y sesiones de DevOps',
        highlights: ['AWS Solutions Architect Pro', 'GenAI Integration', 'High Scalability'],
        skills: ['AWS / GCP', 'Kubernetes & Docker', 'OpenAI & LangChain', 'Ciberseguridad'],
        achievements: [
            'Lideró la migración a nube sin tiempo de inactividad de una fintech con 500k transacciones/día.',
            'Speaker frecuente en AWS Community Day Bolivia.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/rodrigo-penaranda-ejemplo',
            email: 'rpenaranda.tech@comarca.bo',
            whatsapp: '+59170010009'
        }
    },
    {
        id: 'm4', type: 'mentor',
        name: 'Lic. Andrea Beltrán',
        role: 'Mentora de Growth & Retención',
        area: 'Marketing Digital & Crecimiento',
        bio: 'Growth lead con más de 9 años escalando plataformas digitales en Bolivia, Perú y Colombia. Especialista en optimización de embudos de conversión y experimentación rápida.',
        fullBio: 'Andrea enseña a fundadores a dejar atrás las métricas vanidosas y enfocarse en CAC, LTV y retención de cohortes mediante automatización de marketing y analítica avanzada.',
        since: '2023',
        avatar: '/images/miembros/andrea-beltran.jpg',
        initials: 'AB',
        color: '#FFD97C',
        gradient: 'linear-gradient(135deg, #420f54, #EA8406)',
        location: 'Santa Cruz / La Paz',
        availability: '🟢 Sesiones de diagnóstico de embudo de ventas',
        highlights: ['Ex-Lead Growth Latam', '+280% Crecimiento Promedio', 'CAC/LTV Optimization'],
        skills: ['Product Analytics', 'Growth Loops', 'Performance Ads', 'Mixpanel & Amplitude'],
        achievements: [
            'Escaló una aplicación boliviana de 6,000 a 140,000 usuarios activos mensuales.',
            'Mentora seleccionada en Google for Startups Accelerator.'
        ],
        social: {
            linkedin: 'https://linkedin.com/in/andrea-beltran-ejemplo',
            email: 'abeltran.growth@comarca.bo',
            whatsapp: '+59170010010'
        }
    }
];

const allMembers: Member[] = [...founders, ...leaders, ...team, ...mentors];

// ── Modal de Perfil Detallado & Conexión ──
interface MemberProfileModalProps {
    member: Member | null;
    onClose: () => void;
}

const MemberProfileModal: React.FC<MemberProfileModalProps> = ({ member, onClose }) => {
    const { isDark } = useTheme();
    const [activeTab, setActiveTab] = useState<'profile' | 'message'>('profile');
    const [copiedEmail, setCopiedEmail] = useState(false);

    // Formulario de contacto
    const [formName, setFormName] = useState('');
    const [formEmail, setFormEmail] = useState('');
    const [formTopic, setFormTopic] = useState('Mentoría 1 a 1');
    const [formMessage, setFormMessage] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Escape listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    if (!member) return null;

    const handleCopyEmail = () => {
        const email = member.social?.email || `${member.name.toLowerCase().replace(/\s+/g, '.')}@comarca.bo`;
        navigator.clipboard.writeText(email);
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2500);
    };

    const handleSendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formName.trim() || !formEmail.trim() || !formMessage.trim()) return;
        setIsSubmitted(true);
    };

    const getTypeBadgeText = (type: MemberType) => {
        switch (type) {
            case 'fundador': return 'Co-Fundador/a';
            case 'lider': return 'Liderazgo & Dirección';
            case 'equipo': return 'Equipo Operativo';
            case 'mentor': return 'Mentor & Asesor';
        }
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(11, 6, 20, 0.78)',
                backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px', overflowY: 'auto'
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: isDark ? '#150d24' : '#ffffff',
                    color: isDark ? '#ffffff' : '#0f172a',
                    borderRadius: '28px',
                    width: '100%', maxWidth: '680px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    position: 'relative',
                    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4)',
                    border: isDark ? '1px solid rgba(193, 52, 139, 0.3)' : '1px solid rgba(97, 37, 123, 0.15)',
                    animation: 'modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
            >
                <style>{`
                    @keyframes modalSlideUp {
                        from { opacity: 0; transform: translateY(20px) scale(0.97); }
                        to { opacity: 1; transform: translateY(0) scale(1); }
                    }
                `}</style>

                {/* Header Banner */}
                <div style={{
                    height: '140px',
                    background: member.gradient,
                    position: 'relative',
                    padding: '20px',
                    borderTopLeftRadius: '27px',
                    borderTopRightRadius: '27px'
                }}>
                    <button
                        onClick={onClose}
                        aria-label="Cerrar modal"
                        style={{
                            position: 'absolute', top: '16px', right: '16px',
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: 'rgba(0, 0, 0, 0.35)',
                            backdropFilter: 'blur(6px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', transition: 'background 0.2s'
                        }}
                    >
                        <X size={18} />
                    </button>

                    <div style={{
                        position: 'absolute', top: '16px', left: '20px',
                        background: 'rgba(0, 0, 0, 0.35)',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '100px', padding: '4px 14px',
                        fontSize: '0.74rem', fontWeight: '700', color: '#FFD97C',
                        border: '1px solid rgba(255, 217, 124, 0.3)',
                        textTransform: 'uppercase', letterSpacing: '0.08em'
                    }}>
                        {getTypeBadgeText(member.type)}
                    </div>
                </div>

                {/* Avatar & Identidad */}
                <div style={{ padding: '0 32px', position: 'relative', marginTop: '-50px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                        <div style={{
                            width: '100px', height: '100px', borderRadius: '50%',
                            background: member.gradient,
                            border: isDark ? '4px solid #150d24' : '4px solid #ffffff',
                            overflow: 'hidden', position: 'relative',
                            boxShadow: `0 8px 24px ${member.color}40`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <img
                                src={member.avatar} alt={member.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.9rem', fontWeight: '900', color: '#fff', zIndex: -1 }}>
                                {member.initials}
                            </div>
                        </div>

                        {/* Pestañas Perfil / Mensaje */}
                        <div style={{
                            display: 'flex', background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                            borderRadius: '100px', padding: '4px', gap: '4px'
                        }}>
                            <button
                                onClick={() => setActiveTab('profile')}
                                style={{
                                    border: 'none', background: activeTab === 'profile' ? (isDark ? '#61257B' : '#ffffff') : 'transparent',
                                    color: activeTab === 'profile' ? (isDark ? '#ffffff' : '#61257B') : (isDark ? '#cbd5e1' : '#64748b'),
                                    fontWeight: '700', fontSize: '0.82rem', padding: '7px 18px', borderRadius: '100px',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    boxShadow: activeTab === 'profile' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                Perfil Completo
                            </button>
                            <button
                                onClick={() => setActiveTab('message')}
                                style={{
                                    border: 'none', background: activeTab === 'message' ? (isDark ? '#61257B' : '#ffffff') : 'transparent',
                                    color: activeTab === 'message' ? (isDark ? '#ffffff' : '#61257B') : (isDark ? '#cbd5e1' : '#64748b'),
                                    fontWeight: '700', fontSize: '0.82rem', padding: '7px 18px', borderRadius: '100px',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    boxShadow: activeTab === 'message' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                Enviar Mensaje
                            </button>
                        </div>
                    </div>

                    {/* Nombre y Cargo */}
                    <div style={{ marginTop: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <h3 style={{ fontSize: '1.6rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                                {member.name}
                            </h3>
                            <span style={{
                                background: isDark ? 'rgba(255, 217, 124, 0.15)' : '#fff7ed',
                                border: isDark ? '1px solid rgba(255, 217, 124, 0.3)' : '1px solid #fed7aa',
                                borderRadius: '100px', padding: '2px 10px', fontSize: '0.72rem',
                                color: isDark ? '#FFD97C' : '#ea580c', fontWeight: '700'
                            }}>
                                Desde {member.since}
                            </span>
                        </div>
                        <div style={{ fontSize: '1rem', color: isDark ? '#FFD97C' : '#61257B', fontWeight: '700', marginTop: '2px' }}>
                            {member.role} · <span style={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: '500' }}>{member.area}</span>
                        </div>

                        {/* Ubicación y Disponibilidad */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '10px', fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <MapPin size={15} style={{ color: '#EA8406' }} />
                                <span>{member.location || 'La Paz, Bolivia'}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Sparkles size={15} style={{ color: '#C1348B' }} />
                                <span style={{ color: isDark ? '#f472b6' : '#C1348B', fontWeight: '600' }}>
                                    {member.availability || 'Disponible para la comunidad'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ height: '1px', background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', margin: '24px 32px 0' }} />

                {/* Contenido según pestaña activa */}
                <div style={{ padding: '24px 32px 32px' }}>
                    {activeTab === 'profile' ? (
                        <div>
                            {/* Biografía */}
                            <div style={{ marginBottom: '22px' }}>
                                <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? '#FFD97C' : '#61257B', fontWeight: '800', marginBottom: '8px' }}>
                                    Trayectoria
                                </h4>
                                <p style={{ fontSize: '0.94rem', lineHeight: '1.75', color: isDark ? '#cbd5e1' : '#475569' }}>
                                    {member.fullBio || member.bio}
                                </p>
                            </div>

                            {/* Destacados / Badges */}
                            <div style={{ marginBottom: '22px' }}>
                                <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? '#FFD97C' : '#61257B', fontWeight: '800', marginBottom: '10px' }}>
                                    Puntos Clave
                                </h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {member.highlights.map(h => (
                                        <span key={h} style={{
                                            background: isDark ? 'rgba(193, 52, 139, 0.15)' : '#fdf2f8',
                                            border: isDark ? '1px solid rgba(193, 52, 139, 0.3)' : '1px solid #fbcfe8',
                                            color: isDark ? '#f472b6' : '#be185d',
                                            borderRadius: '100px', padding: '5px 14px', fontSize: '0.78rem', fontWeight: '600'
                                        }}>
                                            ✓ {h}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Habilidades & Especialidades */}
                            {member.skills && member.skills.length > 0 && (
                                <div style={{ marginBottom: '22px' }}>
                                    <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? '#FFD97C' : '#61257B', fontWeight: '800', marginBottom: '10px' }}>
                                        Especialidades & Skills
                                    </h4>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {member.skills.map(s => (
                                            <span key={s} style={{
                                                background: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
                                                border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
                                                color: isDark ? '#cbd5e1' : '#334155',
                                                borderRadius: '8px', padding: '4px 11px', fontSize: '0.76rem', fontWeight: '500'
                                            }}>
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Logros */}
                            {member.achievements && member.achievements.length > 0 && (
                                <div style={{ marginBottom: '28px' }}>
                                    <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? '#FFD97C' : '#61257B', fontWeight: '800', marginBottom: '10px' }}>
                                        Hitos & Aportes
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {member.achievements.map((ach, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                                fontSize: '0.87rem', color: isDark ? '#cbd5e1' : '#475569'
                                            }}>
                                                <Award size={17} style={{ color: '#FFD97C', flexShrink: 0, marginTop: '2px' }} />
                                                <span>{ach}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Botones de acción directa */}
                            <div style={{
                                display: 'flex', flexWrap: 'wrap', gap: '10px',
                                paddingTop: '16px', borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9'
                            }}>
                                <button
                                    onClick={() => setActiveTab('message')}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                        color: '#fff', border: 'none', borderRadius: '12px',
                                        padding: '12px 22px', fontSize: '0.88rem', fontWeight: '700',
                                        cursor: 'pointer', transition: 'transform 0.2s', flex: '1 1 auto', justifyContent: 'center'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <Send size={15} />
                                    Agendar / Enviar Mensaje
                                </button>

                                <button
                                    onClick={handleCopyEmail}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        background: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
                                        color: isDark ? '#ffffff' : '#0f172a',
                                        border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #e2e8f0',
                                        borderRadius: '12px', padding: '12px 18px', fontSize: '0.85rem', fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {copiedEmail ? <CheckCircle2 size={16} style={{ color: '#22c55e' }} /> : <Copy size={16} />}
                                    <span>{copiedEmail ? '¡Correo copiado!' : 'Copiar correo'}</span>
                                </button>

                                {member.social?.linkedin && (
                                    <a
                                        href={member.social.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: '46px', height: '46px', borderRadius: '12px',
                                            background: '#0077b5', color: '#fff', textDecoration: 'none',
                                            transition: 'opacity 0.2s'
                                        }}
                                        title="Ver perfil de LinkedIn"
                                    >
                                        <Linkedin size={18} />
                                    </a>
                                )}

                                {member.social?.whatsapp && (
                                    <a
                                        href={`https://wa.me/${member.social.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${member.name}, te contacto a través de la comunidad de Co Marca.`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: '46px', height: '46px', borderRadius: '12px',
                                            background: '#25D366', color: '#fff', textDecoration: 'none'
                                        }}
                                        title="Chatear por WhatsApp"
                                    >
                                        <MessageCircle size={18} />
                                    </a>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Pestaña: Formulario para enviar mensaje / agendar */
                        <div>
                            {isSubmitted ? (
                                <div style={{
                                    textAlign: 'center', padding: '36px 16px',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px'
                                }}>
                                    <div style={{
                                        width: '64px', height: '64px', borderRadius: '50%',
                                        background: 'rgba(34, 197, 94, 0.15)', border: '2px solid #22c55e',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22c55e'
                                    }}>
                                        <CheckCircle2 size={32} />
                                    </div>
                                    <h4 style={{ fontSize: '1.25rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a' }}>
                                        ¡Solicitud enviada a {member.name}!
                                    </h4>
                                    <p style={{ maxWidth: '440px', fontSize: '0.9rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.6' }}>
                                        Hemos registrado tu propuesta de contacto sobre <strong>{formTopic}</strong>. {member.name} o el equipo de coordinación de Co Marca responderá a <strong>{formEmail}</strong> dentro de las próximas 24 horas.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setIsSubmitted(false);
                                            setActiveTab('profile');
                                        }}
                                        style={{
                                            marginTop: '12px', background: isDark ? '#61257B' : '#61257B',
                                            color: '#fff', border: 'none', borderRadius: '12px',
                                            padding: '10px 24px', fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer'
                                        }}
                                    >
                                        Volver al perfil
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSendMessage} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                    <div style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#475569', marginBottom: '4px' }}>
                                        Completa los datos para conectar directamente con <strong>{member.name}</strong> ({member.role}):
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', marginBottom: '6px', color: isDark ? '#cbd5e1' : '#475569' }}>
                                            Tu Nombre Completo *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formName}
                                            onChange={e => setFormName(e.target.value)}
                                            placeholder="Ej. Valeria Salinas"
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: '10px',
                                                border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                                                background: isDark ? '#0b0614' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                                fontSize: '0.9rem', outline: 'none'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', marginBottom: '6px', color: isDark ? '#cbd5e1' : '#475569' }}>
                                            Tu Correo Electrónico *
                                        </label>
                                        <input
                                            type="email"
                                            required
                                            value={formEmail}
                                            onChange={e => setFormEmail(e.target.value)}
                                            placeholder="valeria@tuempresa.bo"
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: '10px',
                                                border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                                                background: isDark ? '#0b0614' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                                fontSize: '0.9rem', outline: 'none'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', marginBottom: '6px', color: isDark ? '#cbd5e1' : '#475569' }}>
                                            Motivo del Contacto
                                        </label>
                                        <select
                                            value={formTopic}
                                            onChange={e => setFormTopic(e.target.value)}
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: '10px',
                                                border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                                                background: isDark ? '#0b0614' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                                fontSize: '0.9rem', outline: 'none'
                                            }}
                                        >
                                            <option value="Mentoría 1 a 1">Mentoría 1 a 1</option>
                                            <option value="Propuesta de Alianza / Startup">Propuesta de Alianza / Startup</option>
                                            <option value="Consulta sobre Espacios Co Marca">Consulta sobre Espacios Co Marca</option>
                                            <option value="Invitación a Charla o Evento">Invitación a Charla o Evento</option>
                                            <option value="Colaboración Profesional">Colaboración Profesional</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', marginBottom: '6px', color: isDark ? '#cbd5e1' : '#475569' }}>
                                            Tu Mensaje *
                                        </label>
                                        <textarea
                                            required
                                            rows={3}
                                            value={formMessage}
                                            onChange={e => setFormMessage(e.target.value)}
                                            placeholder="Describe brevemente en qué consiste tu consulta o cómo quisieras colaborar..."
                                            style={{
                                                width: '100%', padding: '10px 14px', borderRadius: '10px',
                                                border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                                                background: isDark ? '#0b0614' : '#fff', color: isDark ? '#fff' : '#0f172a',
                                                fontSize: '0.9rem', outline: 'none', resize: 'vertical'
                                            }}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab('profile')}
                                            style={{
                                                background: 'transparent', border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                                                color: isDark ? '#cbd5e1' : '#475569', borderRadius: '12px',
                                                padding: '11px 18px', fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer'
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            style={{
                                                flex: 1, background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                                color: '#fff', border: 'none', borderRadius: '12px',
                                                padding: '11px 20px', fontSize: '0.88rem', fontWeight: '700',
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                            }}
                                        >
                                            <Send size={15} />
                                            Enviar Mensaje Directo
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Founder Card (con click para ver perfil) ──
function FounderCard({ member, index, onSelect }: { member: Member; index: number; onSelect: (m: Member) => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [hovered, setHovered] = useState(false);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView ? (hovered ? 'translateY(-4px)' : 'translateY(0)') : `translateX(${index % 2 === 0 ? '-40px' : '40px'})`,
                opacity: inView ? 1 : 0,
                transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.1}s`,
                width: '100%',
                cursor: 'pointer'
            }}
            onClick={() => onSelect(member)}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="founder-card"
                    style={{
                        background: isDark ? '#150d24' : '#fff',
                        borderRadius: '28px',
                        border: isDark ? '1px solid rgba(193,52,139,0.25)' : (hovered ? `1.5px solid ${member.color}45` : '1.5px solid #f1f5f9'),
                        boxShadow: hovered ? `0 24px 64px ${member.color}22, 0 8px 32px rgba(0,0,0,0.08)` : '0 4px 24px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        display: 'flex', flexWrap: 'wrap',
                        transition: 'all 0.4s cubic-bezier(.16,1,.3,1)',
                        width: '100%',
                        position: 'relative'
                    }}
                >
                    {/* Foto side */}
                    <div style={{
                        flex: '0 0 280px', minHeight: '320px', position: 'relative',
                        background: member.gradient, overflow: 'hidden',
                    }}>
                        <img
                            src={member.avatar} alt={member.name}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.6s cubic-bezier(.16,1,.3,1)', transform: hovered ? 'scale(1.05)' : 'scale(1)' }}
                            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                        {/* Fallback */}
                        <div style={{ position: 'absolute', inset: 0, background: member.gradient, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                            <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '160px', height: '160px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%' }} />
                            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem', fontWeight: '900', color: '#fff' }}>
                                {member.initials}
                            </div>
                            <span style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '100px', padding: '4px 14px', fontSize: '0.75rem', fontWeight: '700', color: '#fff' }}>
                                Co-fundador/a
                            </span>
                        </div>
                        {/* Año badge */}
                        <div style={{ position: 'absolute', bottom: '16px', left: '16px', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', borderRadius: '8px', padding: '5px 12px', fontSize: '0.72rem', fontWeight: '700', color: '#FFD97C' }}>
                            Desde {member.since}
                        </div>
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: '260px', padding: '36px 32px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : member.color }}>{member.area}</span>
                            <div style={{ height: '2px', width: '28px', borderRadius: '2px', background: isDark ? '#FFD97C' : member.color }} />
                            <span style={{ background: isDark ? 'rgba(193,52,139,0.2)' : `${member.color}14`, border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : `1.5px solid ${member.color}28`, borderRadius: '100px', padding: '2px 10px', fontSize: '0.68rem', fontWeight: '700', color: isDark ? '#f472b6' : member.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Co-Fundador/a
                            </span>
                        </div>
                        <h3 style={{ fontSize: 'clamp(1.3rem, 2.2vw, 1.7rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '6px' }}>
                            {member.name}
                        </h3>
                        <div style={{ fontSize: '0.92rem', color: isDark ? '#FFD97C' : member.color, fontWeight: '700', marginBottom: '14px' }}>
                            {member.role}
                        </div>
                        <p style={{ fontSize: '0.91rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.75', marginBottom: '20px' }}>
                            {member.bio}
                        </p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '18px' }}>
                            {member.highlights.map(h => (
                                <span key={h} style={{
                                    background: isDark ? 'rgba(255,255,255,0.06)' : `${member.color}10`,
                                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : `1.5px solid ${member.color}22`,
                                    borderRadius: '100px', padding: '4px 12px',
                                    fontSize: '0.74rem', fontWeight: '600',
                                    color: isDark ? '#FFD97C' : member.color,
                                }}>
                                    {h}
                                </span>
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.84rem', fontWeight: '700', color: isDark ? '#f472b6' : '#61257B' }}>
                            <span>Ver perfil completo y conectar</span>
                            <ArrowRight size={14} />
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Leader Card (con click para ver perfil) ──
function LeaderCard({ member, index, onSelect }: { member: Member; index: number; onSelect: (m: Member) => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.08);
    const [hovered, setHovered] = useState(false);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView ? (hovered ? 'translateY(-6px)' : 'translateY(0)') : 'translateY(28px)',
                opacity: inView ? 1 : 0,
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.08}s`,
                width: '100%',
                cursor: 'pointer'
            }}
            onClick={() => onSelect(member)}
        >
            <BorderGlow
                borderRadius={24}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{
                        background: isDark ? '#150d24' : '#fff',
                        borderRadius: '24px',
                        border: isDark ? '1px solid rgba(193,52,139,0.25)' : (hovered ? `1.5px solid ${member.color}45` : '1.5px solid #f1f5f9'),
                        boxShadow: hovered ? `0 16px 48px ${member.color}20` : '0 2px 16px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                        width: '100%',
                        display: 'flex', flexDirection: 'column', height: '100%'
                    }}
                >
                    {/* Top gradient banner con foto */}
                    <div style={{ height: '150px', background: member.gradient, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%' }} />
                        <img
                            src={member.avatar} alt={member.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                        {/* Año */}
                        <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)', borderRadius: '6px', padding: '3px 10px', fontSize: '0.7rem', fontWeight: '700', color: '#FFD97C' }}>
                            Desde {member.since}
                        </div>
                    </div>

                    {/* Avatar floating */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-36px', position: 'relative', zIndex: 1 }}>
                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', border: isDark ? '3px solid #150d24' : '3px solid #fff', overflow: 'hidden', background: member.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 20px ${member.color}30` }}>
                            <img src={member.avatar} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: '900', color: '#fff', zIndex: -1 }}>{member.initials}</div>
                        </div>
                    </div>

                    {/* Content */}
                    <div style={{ padding: '12px 22px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: '700', color: isDark ? '#FFD97C' : member.color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>{member.area}</div>
                        <h3 style={{ fontSize: '1.08rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.2', marginBottom: '4px' }}>{member.name}</h3>
                        <div style={{ fontSize: '0.82rem', color: isDark ? '#FFD97C' : '#61257B', fontWeight: '700', marginBottom: '12px' }}>{member.role}</div>
                        <p style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.65', marginBottom: '14px', flex: 1 }}>{member.bio}</p>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center', marginBottom: '16px' }}>
                            {member.highlights.slice(0, 2).map(h => (
                                <span key={h} style={{
                                    background: isDark ? 'rgba(255,255,255,0.06)' : `${member.color}10`,
                                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : `1px solid ${member.color}20`,
                                    borderRadius: '6px', padding: '3px 8px',
                                    fontSize: '0.68rem', fontWeight: '600',
                                    color: isDark ? '#FFD97C' : member.color,
                                }}>
                                    {h}
                                </span>
                            ))}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontSize: '0.78rem', fontWeight: '700', color: isDark ? '#f472b6' : '#C1348B' }}>
                            <span>Ver perfil</span>
                            <ArrowRight size={13} />
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Team Card (con click para ver perfil) ──
function TeamCard({ member, index, onSelect }: { member: Member; index: number; onSelect: (m: Member) => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.05);
    const [hovered, setHovered] = useState(false);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView ? (hovered ? 'translateY(-5px)' : 'translateY(0)') : 'translateY(20px)',
                opacity: inView ? 1 : 0,
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.05}s`,
                width: '100%',
                cursor: 'pointer'
            }}
            onClick={() => onSelect(member)}
        >
            <BorderGlow
                borderRadius={20}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{
                        background: isDark ? '#150d24' : '#fff',
                        borderRadius: '20px',
                        border: isDark ? '1px solid rgba(193,52,139,0.25)' : (hovered ? `1.5px solid ${member.color}40` : '1.5px solid #f1f5f9'),
                        boxShadow: hovered ? `0 12px 36px ${member.color}16` : '0 2px 10px rgba(0,0,0,0.04)',
                        padding: '22px 16px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', textAlign: 'center',
                        transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    {/* Avatar */}
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', position: 'relative', flexShrink: 0 }}>
                        <img src={member.avatar} alt={member.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top' }}
                            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: member.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: '900', color: '#fff', zIndex: -1 }}>
                            {member.initials}
                        </div>
                        <div style={{ position: 'absolute', inset: '-3px', borderRadius: '50%', border: `2.5px solid ${hovered ? member.color : member.color + '30'}`, transition: 'border-color 0.3s' }} />
                    </div>

                    <div>
                        <div style={{ fontWeight: '800', fontSize: '0.94rem', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '2px' }}>{member.name}</div>
                        <div style={{ fontSize: '0.78rem', color: isDark ? '#FFD97C' : member.color, fontWeight: '700', marginBottom: '2px' }}>{member.role}</div>
                        <div style={{ fontSize: '0.72rem', color: isDark ? '#cbd5e1' : '#94a3b8' }}>Desde {member.since}</div>
                    </div>

                    <p style={{ fontSize: '0.78rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.6', flex: 1 }}>{member.bio}</p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                        {member.highlights.slice(0, 2).map(h => (
                            <span key={h} style={{
                                background: isDark ? 'rgba(255,255,255,0.06)' : `${member.color}10`,
                                borderRadius: '6px', padding: '2px 8px',
                                fontSize: '0.65rem', fontWeight: '600',
                                color: isDark ? '#FFD97C' : member.color,
                            }}>
                                {h}
                            </span>
                        ))}
                    </div>

                    <div style={{ fontSize: '0.74rem', color: isDark ? '#f472b6' : '#61257B', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <span>Conectar</span>
                        <ArrowRight size={12} />
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Mentor Card (Especial para red asesora) ──
function MentorCard({ member, index, onSelect }: { member: Member; index: number; onSelect: (m: Member) => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.08);
    const [hovered, setHovered] = useState(false);

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transform: inView ? (hovered ? 'translateY(-6px)' : 'translateY(0)') : 'translateY(24px)',
                opacity: inView ? 1 : 0,
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.08}s`,
                width: '100%',
                cursor: 'pointer'
            }}
            onClick={() => onSelect(member)}
        >
            <BorderGlow
                borderRadius={24}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{
                        background: isDark ? '#150d24' : '#fff',
                        borderRadius: '24px',
                        border: isDark ? '1px solid rgba(234,132,6,0.25)' : (hovered ? `1.5px solid ${member.color}45` : '1.5px solid #f1f5f9'),
                        boxShadow: hovered ? '0 18px 45px rgba(234,132,6,0.18)' : '0 2px 14px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        padding: '24px',
                        display: 'flex', flexDirection: 'column', height: '100%',
                        transition: 'all 0.35s cubic-bezier(.16,1,.3,1)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '14px' }}>
                        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: member.gradient, overflow: 'hidden', flexShrink: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
                            <img src={member.avatar} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: '900', color: '#fff', zIndex: -1 }}>
                                {member.initials}
                            </div>
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <span style={{
                                    background: isDark ? 'rgba(234,132,6,0.18)' : '#fff7ed',
                                    border: isDark ? '1px solid rgba(234,132,6,0.35)' : '1px solid #fed7aa',
                                    color: isDark ? '#FFD97C' : '#ea580c',
                                    fontSize: '0.68rem', fontWeight: '700', padding: '2px 8px', borderRadius: '100px', textTransform: 'uppercase'
                                }}>
                                    Mentor & Asesor
                                </span>
                                <span style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>
                                    Desde {member.since}
                                </span>
                            </div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginTop: '4px' }}>
                                {member.name}
                            </h3>
                            <div style={{ fontSize: '0.82rem', color: isDark ? '#FFD97C' : '#EA8406', fontWeight: '700' }}>
                                {member.role}
                            </div>
                        </div>
                    </div>

                    <p style={{ fontSize: '0.84rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.65', marginBottom: '14px', flex: 1 }}>
                        {member.bio}
                    </p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '16px' }}>
                        {member.highlights.map(h => (
                            <span key={h} style={{
                                background: isDark ? 'rgba(255,255,255,0.06)' : '#fff7ed',
                                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #ffedd5',
                                borderRadius: '6px', padding: '3px 9px',
                                fontSize: '0.68rem', fontWeight: '600',
                                color: isDark ? '#FFD97C' : '#c2410c'
                            }}>
                                {h}
                            </span>
                        ))}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9', paddingTop: '12px', marginTop: 'auto' }}>
                        <span style={{ fontSize: '0.74rem', color: isDark ? '#a7f3d0' : '#059669', fontWeight: '700' }}>
                            ● Disponible
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem', fontWeight: '700', color: isDark ? '#FFD97C' : '#EA8406' }}>
                            <span>Agendar mentoría</span>
                            <ArrowRight size={13} />
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Section Label ──
function SectionLabel({ title, subtitle, color }: { title: string; subtitle: string; color: string }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} style={{
            marginBottom: '32px',
            opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.65s cubic-bezier(.16,1,.3,1)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : color }}>{subtitle}</span>
                <div style={{ height: '2px', width: '48px', borderRadius: '2px', background: isDark ? '#FFD97C' : color }} />
            </div>
            <h2 style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                {title}
            </h2>
        </div>
    );
}

// ── Hero ──
function HeroSection() {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => { const t = setTimeout(() => setForced(true), 100); return () => clearTimeout(t); }, []);
    const visible = inView || forced;

    return (
        <section style={{
            background: isDark
                ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                : 'linear-gradient(135deg, #fff 0%, #faf5fc 55%, #f5e8fa 100%)',
            padding: 'clamp(100px, 14vw, 140px) 32px clamp(50px, 7vw, 70px)',
            position: 'relative', overflow: 'hidden',
        }}>
            <div className="blob-anim" style={{
                position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px',
                background: isDark
                    ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))'
                    : 'linear-gradient(135deg, rgba(97,37,123,0.14), rgba(193,52,139,0.08))',
                pointerEvents: 'none',
            }} />
            {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
            <HexagonCornerDecoration
                variant="horizontal-degrade"
                direction="left-to-right"
                opacity={isDark ? 0.70 : 0.50}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <div ref={ref as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                <h1 className={`fade-up-miembros d1 ${visible ? 'in' : ''}`} style={{
                    fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', fontWeight: '900',
                    color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.08', letterSpacing: '-0.025em', marginBottom: '12px',
                }}>
                    Nuestro <span style={{
                        background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>Equipo & Mentores</span>
                </h1>
                <h2 className={`fade-up-miembros d1 ${visible ? 'in' : ''}`} style={{
                    fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: '600',
                    color: isDark ? '#cbd5e1' : '#475569', letterSpacing: '-0.01em', marginBottom: '20px',
                }}>
                    Las mentes que potencian la comunidad de <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>Co Marca</span>
                </h2>
                <p className={`fade-up-miembros d2 ${visible ? 'in' : ''}`} style={{
                    fontSize: '1.05rem', color: isDark ? '#94a3b8' : '#64748b',
                    maxWidth: '640px', margin: '0 auto', lineHeight: '1.75', textAlign: 'justify',
                }}>
                    Conecta con nuestros fundadores, equipo operativo y mentores de la red asesora. Haz clic en cualquier miembro para ver su trayectoria completa, sus habilidades clave o agendar una reunión 1 a 1.
                </p>
                <div className={`fade-up-miembros d3 ${visible ? 'in' : ''}`} style={{ display: 'flex', justifyContent: 'center', marginTop: '36px', flexWrap: 'wrap' }}>
                    {[
                        { value: '2', label: 'Co-fundadores', color: isDark ? '#FFD97C' : '#61257B' },
                        { value: '4', label: 'Directores de Área', color: isDark ? '#C1348B' : '#C1348B' },
                        { value: '6', label: 'Equipo Operativo', color: isDark ? '#EA8406' : '#EA8406' },
                        { value: '4+', label: 'Mentores & Red Ángel', color: isDark ? '#FFD97C' : '#61257B' }
                    ].map((s, i) => (
                        <div key={s.label} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 30px',
                            borderRight: i < 3 ? (isDark ? '1.5px solid rgba(193,52,139,0.25)' : '1.5px solid #f1f5f9') : 'none',
                        }}>
                            <div style={{ fontSize: '2.4rem', fontWeight: '900', color: s.color, lineHeight: 1, letterSpacing: '-0.03em' }}>{s.value}</div>
                            <div style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '6px' }}>{s.label}</div>
                        </div>
                    ))}
                </div>
                <div className={`fade-up-miembros d3 ${visible ? 'in' : ''}`} style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes
                        variant="scroll-prompt"
                        size="md"
                        promptText="Conoce y conecta con nuestro equipo"
                    />
                </div>
            </div>
        </section>
    );
}

// ── CTA Final ──
function CtaBanner() {
    const [ref, inView] = useInView(0.2);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} style={{
            marginTop: '64px', borderRadius: '28px',
            background: 'linear-gradient(135deg, #420f54 0%, #61257B 50%, #C1348B 100%)',
            padding: '56px 48px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '28px',
            opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.75s cubic-bezier(.16,1,.3,1)', position: 'relative', overflow: 'hidden',
            boxShadow: '0 20px 48px rgba(97,37,123,0.25)',
        }}>
            <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '280px', height: '280px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />
            <div style={{ position: 'relative' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFD97C', marginBottom: '10px' }}>¿Quieres formar parte?</div>
                <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '800', color: '#fff', lineHeight: '1.15', letterSpacing: '-0.02em' }}>Únete al equipo Co Marca</h3>
                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.88)', marginTop: '10px', maxWidth: '460px', lineHeight: '1.7', textAlign: 'justify' }}>
                    Buscamos personas apasionadas por el emprendimiento boliviano. Si eres mentor, facilitador o líder operativo, queremos conocerte.
                </p>
            </div>
            <a href="/contacto" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: '#fff', color: '#61257B', borderRadius: '14px', padding: '14px 32px',
                fontWeight: '800', fontSize: '0.97rem', textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)', transition: 'all 0.22s', flexShrink: 0,
            }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)'; }}
            >
                Trabajar con nosotros →
            </a>
        </div>
    );
}

// ── Componente Principal ──
const ComunidadMiembros: React.FC = () => {
    const { isDark } = useTheme();

    // Estados interactivos
    const [selectedRole, setSelectedRole] = useState<'todos' | 'fundador' | 'lider' | 'equipo' | 'mentor'>('todos');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMember, setSelectedMember] = useState<Member | null>(null);

    // Conteo por categoría
    const counts = useMemo(() => {
        return {
            todos: allMembers.length,
            fundador: founders.length,
            lider: leaders.length,
            equipo: team.length,
            mentor: mentors.length,
        };
    }, []);

    // Filtrado interactivo
    const filteredMembers = useMemo(() => {
        return allMembers.filter(m => {
            const matchesRole = selectedRole === 'todos' || m.type === selectedRole;
            if (!matchesRole) return false;

            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase().trim();
            const inName = m.name.toLowerCase().includes(q);
            const inRole = m.role.toLowerCase().includes(q);
            const inArea = m.area.toLowerCase().includes(q);
            const inBio = m.bio.toLowerCase().includes(q);
            const inHighlights = m.highlights.some(h => h.toLowerCase().includes(q));
            const inSkills = m.skills?.some(s => s.toLowerCase().includes(q)) ?? false;

            return inName || inRole || inArea || inBio || inHighlights || inSkills;
        });
    }, [selectedRole, searchQuery]);

    const isFiltering = selectedRole !== 'todos' || searchQuery.trim().length > 0;

    return (
        <div style={{
            fontFamily: "'DM Sans', sans-serif",
            background: isDark ? '#0b0614' : '#f8fafc',
            minHeight: '100vh',
            transition: 'background 0.3s ease',
        }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim { animation: blobFloat 9s ease-in-out infinite; }
        .fade-up-miembros { opacity: 0; transform: translateY(28px); transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1); }
        .fade-up-miembros.in { opacity: 1; transform: translateY(0); }
        .fade-up-miembros.d1 { transition-delay: 0.1s; }
        .fade-up-miembros.d2 { transition-delay: 0.2s; }
        .fade-up-miembros.d3 { transition-delay: 0.3s; }
        @media (max-width: 900px) {
          .founder-card { flex-direction: column !important; }
        }
      `}</style>

            <HeroSection />

            {/* ── Barra Interactiva: Búsqueda y Filtros de Rol ── */}
            <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
                <div style={{
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '24px',
                    padding: '24px 28px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
                    border: isDark ? '1px solid rgba(193,52,139,0.25)' : '1px solid rgba(97,37,123,0.1)',
                    display: 'flex', flexDirection: 'column', gap: '20px'
                }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
                        {/* Buscador */}
                        <div style={{
                            position: 'relative', flex: '1 1 320px', minWidth: '260px'
                        }}>
                            <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: isDark ? '#FFD97C' : '#61257B' }} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Buscar por nombre, cargo, especialidad o habilidad..."
                                style={{
                                    width: '100%', padding: '12px 42px 12px 46px',
                                    borderRadius: '100px',
                                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
                                    background: isDark ? '#0b0614' : '#f8fafc',
                                    color: isDark ? '#ffffff' : '#0f172a',
                                    fontSize: '0.9rem', outline: 'none'
                                }}
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    style={{
                                        position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                        background: 'transparent', border: 'none', color: isDark ? '#cbd5e1' : '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        {/* Cantidad de resultados */}
                        <div style={{ fontSize: '0.86rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '600' }}>
                            Mostrando <strong style={{ color: isDark ? '#FFD97C' : '#61257B' }}>{filteredMembers.length}</strong> de {allMembers.length} personas
                        </div>
                    </div>

                    {/* Botones de Filtro por Rol */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {[
                            { key: 'todos', label: 'Todos los Miembros', count: counts.todos },
                            { key: 'fundador', label: 'Fundadores', count: counts.fundador },
                            { key: 'lider', label: 'Líderes & Directores', count: counts.lider },
                            { key: 'equipo', label: 'Equipo Operativo', count: counts.equipo },
                            { key: 'mentor', label: 'Mentores & Red Ángel', count: counts.mentor },
                        ].map(f => {
                            const active = selectedRole === f.key;
                            return (
                                <button
                                    key={f.key}
                                    onClick={() => setSelectedRole(f.key as any)}
                                    style={{
                                        background: active
                                            ? 'linear-gradient(135deg, #61257B, #C1348B)'
                                            : (isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc'),
                                        color: active ? '#ffffff' : (isDark ? '#cbd5e1' : '#475569'),
                                        border: active
                                            ? 'none'
                                            : (isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0'),
                                        borderRadius: '100px',
                                        padding: '8px 18px',
                                        fontSize: '0.82rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        boxShadow: active ? '0 4px 14px rgba(97,37,123,0.3)' : 'none'
                                    }}
                                >
                                    <span>{f.label}</span>
                                    <span style={{
                                        background: active ? 'rgba(255,255,255,0.22)' : (isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                                        color: active ? '#ffffff' : (isDark ? '#cbd5e1' : '#64748b'),
                                        borderRadius: '100px', padding: '1px 7px', fontSize: '0.72rem'
                                    }}>
                                        {f.count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── Lista de Miembros ── */}
            <section style={{ padding: '40px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>

                {/* Si estamos filtrando activamente o buscando */}
                {isFiltering ? (
                    <div>
                        {filteredMembers.length === 0 ? (
                            <div style={{
                                textAlign: 'center', padding: '60px 20px',
                                background: isDark ? '#150d24' : '#ffffff',
                                borderRadius: '24px', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'
                            }}>
                                <Users size={48} style={{ color: isDark ? '#FFD97C' : '#61257B', margin: '0 auto 16px', opacity: 0.7 }} />
                                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '8px' }}>
                                    No encontramos miembros que coincidan
                                </h3>
                                <p style={{ fontSize: '0.92rem', color: isDark ? '#94a3b8' : '#64748b', maxWidth: '420px', margin: '0 auto 20px' }}>
                                    No hay resultados para "{searchQuery}". Intenta con otros términos como "fintech", "legal", "cloud" o restablece los filtros.
                                </p>
                                <button
                                    onClick={() => { setSelectedRole('todos'); setSearchQuery(''); }}
                                    style={{
                                        background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                        color: '#fff', border: 'none', borderRadius: '12px',
                                        padding: '10px 24px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer'
                                    }}
                                >
                                    Restablecer filtros
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div style={{ fontSize: '0.9rem', color: isDark ? '#94a3b8' : '#64748b', marginBottom: '24px' }}>
                                    Mostrando {filteredMembers.length} resultados filtrados:
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '24px'
                                }}>
                                    {filteredMembers.map((m, i) => {
                                        if (m.type === 'mentor') {
                                            return <MentorCard key={m.id} member={m} index={i} onSelect={setSelectedMember} />;
                                        }
                                        return <LeaderCard key={m.id} member={m} index={i} onSelect={setSelectedMember} />;
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Vista jerárquica original organizada cuando no hay filtro */
                    <>
                        {/* ── Fundadores ── */}
                        <SectionLabel title="Los Co-fundadores" subtitle="Quiénes somos" color="#61257B" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', marginBottom: '64px' }}>
                            {founders.map((m, i) => (
                                <FounderCard key={m.id} member={m} index={i} onSelect={setSelectedMember} />
                            ))}
                        </div>

                        {/* ── Líderes ── */}
                        <SectionLabel title="Equipo Directivo" subtitle="Liderazgo" color="#C1348B" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '24px', marginBottom: '64px' }}>
                            {leaders.map((m, i) => (
                                <LeaderCard key={m.id} member={m} index={i} onSelect={setSelectedMember} />
                            ))}
                        </div>

                        {/* ── Mentores & Red Asesora ── */}
                        <SectionLabel title="Mentores & Red Ángel" subtitle="Asesoría de Alto Impacto" color="#EA8406" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px', marginBottom: '64px' }}>
                            {mentors.map((m, i) => (
                                <MentorCard key={m.id} member={m} index={i} onSelect={setSelectedMember} />
                            ))}
                        </div>

                        {/* ── Equipo operativo ── */}
                        <SectionLabel title="Equipo Operativo" subtitle="El día a día" color="#61257B" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                            {team.map((m, i) => (
                                <TeamCard key={m.id} member={m} index={i} onSelect={setSelectedMember} />
                            ))}
                        </div>
                    </>
                )}

                <CtaBanner />
            </section>

            {/* Modal de perfil interactivo */}
            <MemberProfileModal
                member={selectedMember}
                onClose={() => setSelectedMember(null)}
            />
        </div>
    );
};

export default ComunidadMiembros;